// StatusPill.jsx
// Soft, modern status pill (tinted fill + colored text + a small status dot).
// Replaces the legacy solid-fill `getStatusStyle` chips so workspace, job and
// pod statuses read consistently and stay aligned with the token palette.
import PropTypes from "prop-types";
import { Box, Tooltip } from "@mui/material";
import { STATUS, FONT, WEIGHT, withAlpha } from "../../../../theme/tokens";

// Maps the semantic color keys already used throughout the tables
// (success / warning / error / info / default) onto token colors.
const COLOR_FOR = {
  success: STATUS.success,
  warning: STATUS.warning,
  error: STATUS.error,
  info: STATUS.info,
  default: STATUS.neutral,
};

export default function StatusPill({ label, color = "default" }) {
  const c = COLOR_FOR[color] || STATUS.neutral;
  return (
    <Tooltip title={label}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.625,
          maxWidth: "100%",
          px: 1,
          py: 0.375,
          borderRadius: "999px",
          bgcolor: withAlpha(c, 0.12),
          color: c,
          fontSize: FONT.xs,
          fontWeight: WEIGHT.semibold,
          lineHeight: 1.4,
        }}
      >
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: c,
            flexShrink: 0,
          }}
        />
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
}

StatusPill.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.oneOf(["success", "warning", "error", "info", "default"]),
};
